import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import {
  Label as FieldIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

import {
  Widget,
  FieldKVWidgetConfiguration,
} from '../../../types/canvas.types';
import { WidgetWrapper } from './WidgetWrapper';
import { useWidgetData } from '../../../data/canvas/useWidgetData';
import { useAccount } from '../../../auth';

interface FieldKVWidgetProps {
  widget: Widget;
  onEdit?: (widgetId: string) => void;
  onDelete?: (widgetId: string) => void;
  onDuplicate?: (widgetId: string) => void;
}

export const FieldKVWidget: React.FC<FieldKVWidgetProps> = ({
  widget,
  onEdit,
  onDelete,
  onDuplicate,
}) => {
  const { currentAccount } = useAccount();
  const accountId = currentAccount?.id || '';
  const config = widget.configuration as FieldKVWidgetConfiguration;

  const {
    data: result,
    isLoading,
    error,
    refetch,
  } = useWidgetData(widget, accountId, {
    enabled: !!accountId && !!widget.data_source?.['entity_name'],
    pagination: { page: 0, pageSize: 1 },
  });

  // Build key-value pairs from the first row of data, or from configured fields
  const buildFields = () => {
    const row = result?.data?.[0];
    if (!row) return [];

    // If fields are configured, use those as the display labels/keys
    if (config?.fields && config.fields.length > 0) {
      return config.fields.map((field) => ({
        label: field.label || field.key,
        value: row[field.key] != null ? String(row[field.key]) : '-',
      }));
    }

    // Otherwise, show all columns from the first row
    return Object.entries(row).map(([key, value]) => ({
      label: key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
      value: value != null ? String(value) : '-',
    }));
  };

  const fields = buildFields();

  return (
    <WidgetWrapper
      widget={widget}
      onEdit={onEdit}
      onDelete={onDelete}
      onDuplicate={onDuplicate}
    >
      {isLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={150}
        >
          <CircularProgress size={32} />
        </Box>
      ) : error ? (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => refetch()}
            >
              Retry
            </Button>
          }
        >
          Failed to load data
        </Alert>
      ) : fields.length > 0 ? (
        <Grid container spacing={2}>
          {fields.map((field, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {field.label}
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {field.value}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight={200}
          textAlign="center"
        >
          <FieldIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography variant="body1" color="text.secondary">
            No field data
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure a data source to display key-value pairs
          </Typography>
        </Box>
      )}
    </WidgetWrapper>
  );
};
